/**
 * Client-side logging modul.
 *
 * Kombinira:
 * - Pino logger za console output
 * - localStorage queue za pouzdano slanje logova na server
 * - Automatsko hvatanje window error/unhandledrejection
 *
 * Korištenje:
 *   import { initClientLogging, logClient } from 'src/lib/logger.client';
 *
 *   // U _app.tsx ili root layout-u (jednom):
 *   initClientLogging();
 *
 *   // Bilo gdje u klijentu:
 *   logClient.error(error, { context: 'UserProfile' });
 *   logClient.info('User clicked button', { buttonId: 'submit' });
 */

import pino from 'pino';
import { getSession } from 'next-auth/react';

import { formatErrorForLog, prepareLogPayload } from './log-utils';

import type { LogLevel, LogPayload } from './log-utils';

// ---------------------------------------------------------------------------
// Pino Console Logger
// ---------------------------------------------------------------------------

const pinoLogger = pino({
  browser: { asObject: true },
  level: process.env.LOG_LEVEL || 'info',
});

// ---------------------------------------------------------------------------
// LocalStorage Queue
// ---------------------------------------------------------------------------

interface QueuedLog {
  id: string;
  createdAt: number;
  attempts: number;
  payload: LogPayload;
}

const QUEUE_KEY = '__export_city_log_queue_v2';
const LOCK_KEY = '__export_city_log_queue_lock_v2';
const LOCK_TTL_MS = 5_000;
const MAX_QUEUE_SIZE = 100;
const MAX_ATTEMPTS = 6;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function readQueue(): QueuedLog[] {
  if (!isBrowser()) return [];
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const value = JSON.parse(raw);
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeQueue(entries: QueuedLog[]): void {
  if (!isBrowser()) return;
  try {
    // Ograniči veličinu queue-a
    const limited = entries.slice(-MAX_QUEUE_SIZE);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(limited));
  } catch {
    // localStorage pun ili nedostupan
  }
}

function tryAcquireLock(): boolean {
  if (!isBrowser()) return false;
  const now = Date.now();
  try {
    const lockRaw = localStorage.getItem(LOCK_KEY);
    const lockUntil = lockRaw ? Number(lockRaw) : 0;
    if (Number.isFinite(lockUntil) && lockUntil > now) return false;
    localStorage.setItem(LOCK_KEY, String(now + LOCK_TTL_MS));
    return true;
  } catch {
    return false;
  }
}

function releaseLock(): void {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(LOCK_KEY);
  } catch {
    // ignore
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Queue Operations
// ---------------------------------------------------------------------------

function enqueue(payload: LogPayload): void {
  if (!isBrowser()) return;
  const entry: QueuedLog = {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    createdAt: Date.now(),
    attempts: 0,
    payload,
  };
  const queue = readQueue();
  queue.push(entry);
  writeQueue(queue);
}

async function postLog(payload: LogPayload): Promise<boolean> {
  try {
    const res = await fetch('/api/logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify(payload),
    });
    return res.ok || res.status === 202; // 202 = queued
  } catch {
    return false;
  }
}

async function flushQueue(): Promise<void> {
  if (!isBrowser()) return;
  if (!tryAcquireLock()) return;

  try {
    let queue = readQueue();
    if (queue.length === 0) return;

    const toSend = queue.slice(0, 20);
    for (const entry of toSend) {
      const success = await postLog(entry.payload);
      if (success) {
        queue = queue.filter((q) => q.id !== entry.id);
        writeQueue(queue);
      } else {
        const nextAttempts = entry.attempts + 1;
        if (nextAttempts >= MAX_ATTEMPTS) {
          // Ukloni nakon max pokušaja
          queue = queue.filter((q) => q.id !== entry.id);
          writeQueue(queue);
        } else {
          queue = queue.map((q) => (q.id === entry.id ? { ...q, attempts: nextAttempts } : q));
          writeQueue(queue);
          const backoffMs = Math.min(30_000, 250 * 2 ** Math.min(nextAttempts, 10));
          await sleep(backoffMs);
        }
      }
    }
  } finally {
    releaseLock();
  }
}

// ---------------------------------------------------------------------------
// Core Logging Functions
// ---------------------------------------------------------------------------

async function getUserId(): Promise<number | undefined> {
  try {
    const session = await getSession();
    return session?.user?.id ? Number(session.user.id) : undefined;
  } catch {
    return undefined;
  }
}

function getClientContext(): { url: string; userAgent: string; environment: string } {
  if (!isBrowser()) {
    return { url: '', userAgent: '', environment: process.env.NODE_ENV || 'unknown' };
  }
  return {
    url: window.location.href,
    userAgent: navigator.userAgent,
    environment: process.env.ENVIRONMENT || process.env.NODE_ENV || 'unknown',
  };
}

async function log(
  level: LogLevel,
  errorOrMessage: unknown,
  meta?: Record<string, unknown>
): Promise<void> {
  const message = formatErrorForLog(errorOrMessage);
  const context = getClientContext();

  // Console log
  switch (level) {
    case 'info':
      pinoLogger.info({ ...meta, ...context }, message);
      break;
    case 'warn':
      pinoLogger.warn({ ...meta, ...context }, message);
      break;
    case 'debug':
      pinoLogger.debug({ ...meta, ...context }, message);
      break;
    default:
      pinoLogger.error({ ...meta, ...context }, message);
  }

  // Enqueue za server
  if (isBrowser()) {
    const userId = await getUserId();
    const payload = prepareLogPayload(errorOrMessage, {
      level,
      meta: { ...meta, userAgent: context.userAgent },
      userId,
      url: context.url,
      method: 'CLIENT',
      environment: context.environment,
    });

    enqueue(payload);
    void flushQueue().catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const logClient = {
  info: (message: unknown, meta?: Record<string, unknown>) => log('info', message, meta),
  warn: (message: unknown, meta?: Record<string, unknown>) => log('warn', message, meta),
  error: (error: unknown, meta?: Record<string, unknown>) => log('error', error, meta),
  debug: (message: unknown, meta?: Record<string, unknown>) => log('debug', message, meta),
};

/**
 * Alias za logClient.error - kompatibilnost sa starim kodom
 */
export const logClientError = (error: unknown, meta?: Record<string, unknown>) =>
  logClient.error(error, meta);

let initialized = false;

/**
 * Inicijalizira client-side error handling.
 * Pozovi jednom u _app.tsx ili root layout komponenti.
 */
export function initClientLogging(): void {
  if (!isBrowser() || initialized) return;
  initialized = true;

  // Flush bilo koje zaostale logove iz prethodnih sesija
  void flushQueue().catch(() => {});

  // Global error handler
  window.addEventListener('error', (event) => {
    logClient.error(event.error || event.message, {
      type: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    logClient.error(event.reason, { type: 'unhandledrejection' });
  });

  // Flush prije zatvaranja stranice
  window.addEventListener('beforeunload', () => {
    void flushQueue().catch(() => {});
  });
}

// Export za kompatibilnost
export { flushQueue as flushClientLogQueue };
